import { type ReactNode, type ComponentPropsWithoutRef, useMemo } from 'react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { usePrefetch } from '@/hooks';
import CTNIcon from '@ctn/ui/components/ctn-icon/icon';

export type SidebarItem = {
	label: string;
	to?: string;
	icon?: ReactNode;
	badge?: string | number;
	children?: SidebarItem[];
	matchDescendants?: boolean;
};

type SidebarProps = Omit<ComponentPropsWithoutRef<'section'>, 'title'> & {
	readonly className?: string;
	/** Ítems del menú lateral */
	readonly items: SidebarItem[];
	/** Slot opcional bajo el menú (bloque extra, filtros, etc.) */
	readonly children?: ReactNode;
};

const classNameComponent = 'c-cnm-sidebar';

/** Ítem recursivo */
const SidebarNode = ({ item }: { readonly item: SidebarItem }) => {
	const hasChildren = (item.children?.length ?? 0) > 0;
	const showBadge = item.badge !== undefined && item.badge !== null && item.badge !== '';
	const { prefetch, cancel } = usePrefetch();
	const location = useLocation();

	const isPathActive = (to: string) => {
		const norm = (s: string) => s.replace(/\/+$/, '');
		const current = norm(location.pathname);
		const base = norm(to);
		return current === base || current.startsWith(`${base}/`);
	};

	return (
		<li className={`${classNameComponent}__item`}>
			{item.to ? (
				(() => {
					const to = item.to ?? '';
					return (
						<NavLink
							to={to}
							end={!item.children?.length}
							className={({ isActive }) => {
								const active = (item as any).matchDescendants ? isPathActive(to) : isActive;
								return `c-cnm-sidebar__link${active ? ' is-active' : ''}`;
							}}
							onMouseEnter={() => prefetch(to)}
							onMouseLeave={cancel}
							onFocus={() => prefetch(to)}
						>
							{({ isActive }) => {
								const active = item.matchDescendants ? isPathActive(to) : isActive;
								return (
									<>
										<div>
											{item.icon && (
												<span className={`${classNameComponent}__icon`}>{item.icon}</span>
											)}
											<span className={`${classNameComponent}__label`}>{item.label}</span>
											{showBadge && (
												<span className={`${classNameComponent}__badge`}>{item.badge}</span>
											)}
										</div>
										{active && (
											<CTNIcon
												className={`${classNameComponent}__arrow`}
												icon="dropdownUp"
												color="yellow-60"
											/>
										)}
									</>
								);
							}}
						</NavLink>
					);
				})()
			) : (
				<span className={`${classNameComponent}__link ${classNameComponent}__link--nolink`}>
					{item.icon && <span className={`${classNameComponent}__icon`}>{item.icon}</span>}
					<span className={`${classNameComponent}__label`}>{item.label}</span>
					{showBadge && <span className={`${classNameComponent}__badge`}>{item.badge}</span>}
				</span>
			)}

			{hasChildren && (
				<ul className={`${classNameComponent}__sublist`} role="list">
					{item.children!.map((child) => (
						<SidebarNode key={`${child.label}-${child.to ?? 'nolink'}`} item={child} />
					))}
				</ul>
			)}
		</li>
	);
};

export const Sidebar = ({ items, className, children, ...rest }: SidebarProps) => {
	const wrapperClassName = useMemo(() => classNames(classNameComponent, className), [className]);

	return (
		<section className={wrapperClassName} {...rest}>
			<nav className={`${classNameComponent}__nav`} aria-label="Sidebar">
				<ul className={`${classNameComponent}__list`} role="list">
					{items.map((it) => (
						<SidebarNode key={`${it.label}-${it.to ?? 'nolink'}`} item={it} />
					))}
				</ul>
			</nav>
			{children ? <div className={`${classNameComponent}__content`}>{children}</div> : null}
		</section>
	);
};
